import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import * as _ from 'lodash';

import * as Edge from '../../core';
import ModalContainer from '../global/modal';
import Loading from '../loading';
import Error from '../global/error';
import { withLoadDataDefaultConfig } from '../loadData';
import errorLoadingWrapperHOC from '../errorLoadingWrapper/errorLoadingWrapperHOC';
import { SessionService } from '../../services/sessionService';
import { CheckoutPageStage, generatePurchasePageLink } from '../../pages/checkout';
import { getPriceTitle } from '../../utilities/commerceUtil';
import { AppState } from '../../store';
import AdminMenu from '../adminMenu';
import { CommerceService } from '../../services/commerceService';
import UpdateCurrentPrice from './updateCurrentPrice';
import ChangeAssignmentsManually from '../../pages/manageOrganization/assignSeats/changeManually';
import { CreditMode } from '../assignCredits';

import './index.scss';
import 'react-datepicker/dist/react-datepicker.css';

export interface YourCurrentPlanProps {
	addSeats?: () => void;
	canEdit?: boolean;
	entityId: string;
	entityType: Edge.Models.EntityType;
	extendSubscription?: () => void;
	isAdmin?: boolean;
	latestPurchasePlan?: Edge.Models.LatestPurchasePlanResponse;
	organization: Edge.Models.Organization;
	reloadData: () => Promise<void>;
}

interface YourCurrentPlanState {
	cancellingSubscription?: boolean;
	changingEndDate?: Date; // the new plan end date
	error?: Edge.Models.EdgeError;
	loading: boolean;
	subscriptionEndDate?: Date;
	updatingCurrentSku?: boolean;
}

export class YourCurrentPlan extends React.Component<YourCurrentPlanProps, YourCurrentPlanState> {
	constructor(props: YourCurrentPlanProps) {
		super(props);
		const { latestPurchasePlan } = this.props;
		const subscription = latestPurchasePlan && latestPurchasePlan.subscription;
		this.state = {
			subscriptionEndDate:
				subscription && subscription.expirationDateUtc
					? moment(subscription.expirationDateUtc)
							.local()
							.toDate()
					: undefined,
			loading: false,
		};
	}
	public render() {
		const { canEdit, entityId, entityType, isAdmin, latestPurchasePlan, organization, reloadData } = this.props;
		const {
			cancellingSubscription,
			changingEndDate,
			error,
			loading,
			subscriptionEndDate,
			updatingCurrentSku,
		} = this.state;
		const creditMode: CreditMode =
			latestPurchasePlan && latestPurchasePlan.subscription ? CreditMode.Subscriptions : CreditMode.Sessions;

		const order3dGlassesButton = () => (
			<Link
				to={this.getLink(Edge.Models.CheckoutSessionType.Initial, CheckoutPageStage.GlassesOption)}
				className="cta_btn"
			>
				Order 3D Glasses
			</Link>
		);

		return (
			<div className="your_current_plan">
				{isAdmin && canEdit && (
					<AdminMenu>
						<button onClick={this.openUpdateCurrentSku}>Update Current Product</button>
					</AdminMenu>
				)}
				{!latestPurchasePlan ? (
					<>
						{entityType === Edge.Models.EntityType.User ? (
							order3dGlassesButton()
						) : (
							<>
								<p className="no_active">You have no active plan.</p>
								{canEdit && (
									<Link
										to={this.getLink(Edge.Models.CheckoutSessionType.Initial)}
										className="cta_btn"
									>
										Find a plan
									</Link>
								)}
							</>
						)}
					</>
				) : (
					<>
						<div className="section_label">Your Current Plan</div>
						<div className="current_plan_header">
							<div className="text_wrapper">
								<h2>{getPriceTitle(latestPurchasePlan.price)}</h2>
							</div>
							<div className="action_wrapper">
								{order3dGlassesButton()}
								{entityType === Edge.Models.EntityType.Organization && (
									<>
										{latestPurchasePlan.subscription &&
											latestPurchasePlan.subscription.isRecurring && (
												<>
													{this.hasSubscriptionExpired(latestPurchasePlan.subscription) ||
													this.isSubscriptionPausing(latestPurchasePlan.subscription) ? (
														<Link
															to={this.getLink(Edge.Models.CheckoutSessionType.Extend)}
															className="cta_btn"
														>
															Restart Subscription
														</Link>
													) : (
														<button
															className="cta_btn btn_destroy"
															onClick={this.openCancelSubscription}
														>
															Cancel Subscription
														</button>
													)}
												</>
											)}
										{canEdit && (
											<Link
												to={this.getLink(Edge.Models.CheckoutSessionType.Initial)}
												className="cta_btn"
											>
												Renew/Upgrade
											</Link>
										)}
									</>
								)}
							</div>
						</div>
						{entityType === Edge.Models.EntityType.Organization && (
							<div>
								{latestPurchasePlan.subscription && (
									<>
										{latestPurchasePlan.subscription.isRecurring && (
											<div className="label_item">
												Status:
												<span>
													{this.getRecurringSubscriptionStatus(
														latestPurchasePlan.subscription
													)}
												</span>
											</div>
										)}
										{!latestPurchasePlan.subscription.isRecurring && (
											<div className="label_item">
												Duration:
												<span>{latestPurchasePlan.subscription.lifetimeDays} days</span>
											</div>
										)}
										{!latestPurchasePlan.subscription.isRecurring && (
											<div className="label_item">
												Seats:
												<span>
													{latestPurchasePlan.subscription.seats}
													{latestPurchasePlan.subscription && canEdit && (
														<Link
															to={this.getLink(Edge.Models.CheckoutSessionType.AddSeats)}
															className="underline"
														>
															Add Seats
														</Link>
													)}
												</span>
											</div>
										)}
										{!latestPurchasePlan.subscription.isRecurring && (
											<div className="label_item">
												Start Date:
												<span>
													{latestPurchasePlan.subscription.startedDateUtc
														? moment(latestPurchasePlan.subscription.startedDateUtc)
																.local()
																.startOf('day')
																.format(Edge.Constants.DATE_FORMAT)
														: 'Not started'}
												</span>
											</div>
										)}
										{latestPurchasePlan.subscription.isRecurring ? (
											!this.hasSubscriptionExpired(latestPurchasePlan.subscription) &&
											!this.isSubscriptionPausing(latestPurchasePlan.subscription) && (
												<div className="label_item">
													Next Billing Date:
													<span>
														{moment(subscriptionEndDate)
															.local()
															.format(Edge.Constants.DATE_FORMAT)}
													</span>
												</div>
											)
										) : (
											<div className="label_item">
												End Date:
												<span>
													{!subscriptionEndDate ? (
														'Not started'
													) : isAdmin && canEdit ? (
														<>
															<DatePicker
																selected={subscriptionEndDate}
																onChange={(date: Date) => {
																	this.setState({ changingEndDate: date });
																}}
															/>
															&nbsp;{moment(subscriptionEndDate).format('hh:mm a')}
														</>
													) : (
														moment(subscriptionEndDate)
															.subtract(1, 'day')
															.format(Edge.Constants.DATE_FORMAT)
													)}
												</span>
												{changingEndDate && (
													<ModalContainer
														open
														title="Are you sure?"
														onClose={this.closeChangingEndDate}
													>
														<p>{`Do you want to change the end date of this plan to ${moment(
															changingEndDate
														).format(Edge.Constants.DATE_FORMAT)}?`}</p>
														{loading && <Loading buttonLoader />}
														{error && <Error>{Edge.API.getErrorMessage(error)}</Error>}
														<button className="cta_btn" onClick={this.setPlanEndDate}>
															Yes
														</button>
														<button
															className="cta_btn btn_light"
															onClick={this.closeChangingEndDate}
														>
															Cancel
														</button>
													</ModalContainer>
												)}
											</div>
										)}
									</>
								)}
								{!latestPurchasePlan.subscription && (
									<div className="label_item">
										Sessions:
										<span>
											{latestPurchasePlan.sessionCreditTotal || 0}
											{canEdit && (
												<Link
													to={this.getLink(Edge.Models.CheckoutSessionType.Initial)}
													className="underline"
												>
													Add Sessions
												</Link>
											)}
										</span>
									</div>
								)}
								{isAdmin && canEdit && (
									<ChangeAssignmentsManually
										currentOrganization={organization}
										mode={creditMode}
										reloadData={reloadData}
									/>
								)}
							</div>
						)}
					</>
				)}
				{cancellingSubscription && (
					<ModalContainer open title="Cancel recurring subscription" onClose={this.closeCancelSubscription}>
						<p>
							You are about to cancel your recurring subscription! Click on the cancel button below to
							confirm this action.
						</p>
						<p>
							Once your plan expires you will no longer have access to exercises, evaluations and
							training. Please note there are no refunds for partial months.
						</p>
						<p>Come back any time by reactivating your subscription!</p>
						{error && <Error>{Edge.API.getErrorMessage(error)}</Error>}
						{loading && <Loading buttonLoader />}
						<button onClick={this.cancelSubscription} className="cta_btn btn_destroy">
							Cancel Subscription
						</button>
					</ModalContainer>
				)}
				{isAdmin && updatingCurrentSku && (
					<ModalContainer open title="Update Current Product" onClose={this.closeUpdateCurrentSku}>
						<UpdateCurrentPrice
							entityId={entityId}
							entityType={entityType}
							organizationId={organization.id}
							updateCurrentPrice={this.updateCurrentSku}
							currentPrice={latestPurchasePlan!.price}
							closeModal={this.closeUpdateCurrentSku}
							reloadParent={reloadData}
						/>
					</ModalContainer>
				)}
			</div>
		);
	}

	private getRecurringSubscriptionStatus = (subscription: Edge.Models.LatestPurchasePlanSubscription): string => {
		const { subscriptionEndDate } = this.state;
		if (this.hasSubscriptionExpired(subscription)) {
			return `Expired on ${moment(subscriptionEndDate)
				.local()
				.format(Edge.Constants.DATE_FORMAT)}`;
		}
		if (this.isSubscriptionPaused(subscription)) {
			return `Paused`;
		}
		if (this.isSubscriptionPausing(subscription)) {
			return `Expires on ${moment(subscriptionEndDate)
				.local()
				.format(Edge.Constants.DATE_FORMAT)}`;
		}
		return 'Active';
	};

	private hasSubscriptionExpired = (subscription?: Edge.Models.LatestPurchasePlanSubscription): boolean =>
		!!subscription &&
		!!subscription.expirationDateUtc &&
		new Date(subscription.expirationDateUtc).getTime() < Date.now();

	private isSubscriptionPaused = (subscription?: Edge.Models.LatestPurchasePlanSubscription): boolean =>
		!!subscription && !!subscription.pausedDateUtc && new Date(subscription.pausedDateUtc).getTime() < Date.now();

	private isSubscriptionPausing = (subscription?: Edge.Models.LatestPurchasePlanSubscription): boolean =>
		!!subscription && !!subscription.pausedDateUtc;

	private getLink = (orderType: Edge.Models.CheckoutSessionType, checkoutPageStage?: CheckoutPageStage) => {
		const { entityType, entityId } = this.props;
		return generatePurchasePageLink(entityType, entityId, orderType, checkoutPageStage);
	};

	private setPlanEndDate = async () => {
		this.setState({ loading: true, error: undefined });
		try {
			const { changingEndDate } = this.state;
			await SessionService.updateSubscriptionEndDate({
				entityId: this.props.entityId,
				entityType: this.props.entityType,
				endDateUtc: moment(changingEndDate!)
					.endOf('day')
					.utc(true)
					.toDate(),
			});
			this.setState({ subscriptionEndDate: changingEndDate, changingEndDate: undefined, loading: false });
		} catch (error) {
			this.setState({ error: error as Edge.Models.EdgeError, loading: false });
		}
	};

	private closeCancelSubscription = () => {
		this.setState({ cancellingSubscription: false });
	};

	private openCancelSubscription = () => {
		this.setState({ cancellingSubscription: true });
	};

	private cancelSubscription = async () => {
		this.setState({ loading: true, error: undefined });
		try {
			await SessionService.cancelRecurringSubscription();
			this.setState({ cancellingSubscription: false, loading: false });
			this.props.reloadData();
		} catch (error) {
			this.setState({ error: error as Edge.Models.EdgeError, loading: false });
		}
	};

	private closeChangingEndDate = () => {
		this.setState({ changingEndDate: undefined, error: undefined });
	};

	private openUpdateCurrentSku = () => {
		this.setState({ updatingCurrentSku: true });
	};

	private updateCurrentSku = async (skuId: string) => {
		const { entityType, entityId } = this.props;
		await CommerceService.updateActivePrice({ entityType, entityId, priceId: skuId });
	};

	private closeUpdateCurrentSku = () => {
		this.setState({ updatingCurrentSku: false });
	};
}

function mapStateToProps({ loginState, organizationState }: AppState) {
	return {
		isAdmin: loginState.decodedToken && loginState.decodedToken.is_admin,
		organization: organizationState.currentOrganization!,
	};
}

export default connect(mapStateToProps)(
	withLoadDataDefaultConfig(
		errorLoadingWrapperHOC(YourCurrentPlan, undefined, undefined, undefined, {
			loadingOptions: { blockItem: true },
		}),
		(props: YourCurrentPlanProps) => _.pick(props, 'entityType', 'entityId', 'organization'),
		async (props) => {
			const latestPurchasePlan = await SessionService.getLatestPurchasePlan(
				props.entityType,
				props.entityId,
				props.organization.id
			);
			return {
				canEdit:
					!latestPurchasePlan || !(latestPurchasePlan.subscription || { isRecurring: false }).isRecurring,
				latestPurchasePlan,
			};
		}
	)
);
